import {
  Page,
  PageBody,
  Spinner,
  ToastContainer,
} from '@pypestream/design-system';
import { ErrorState, OnboardingModal } from '@pypestream/ui-patterns';
import * as Sentry from '@sentry/react';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  useNavigate,
  useParams,
  Navigate,
  useMatch,
  RouterProvider as ReactRouterProvider,
} from 'react-router';

import { PrimaryNav } from './components';
import { ErrorBoundary } from './components/error-boundaries/error-boundary';
import {
  MANAGER_SURFACE_ROLES,
  OrganizationRoleNames,
  useAuthRole,
} from './hooks';
import { IPage } from './pages/types';
import {
  sendManagerEvent,
  sendUserEvent,
  useManagerCtxSelector,
  useUserCtxSelector,
  useUserStateMatchesOneOf,
} from './xstate/app.xstate';

import {
  CreateOrganization,
  Homepage,
  LogsPage,
  MyAccountPage,
  OrganizationSettings,
  OrganizationsPage,
  ProjectDetails,
  ProjectsPage,
  SuperAdminPage,
  TeamsPage,
  UsersPage,
} from './pages';
import { NotFoundPage } from './pages/not-found/not-found-page';
import UserDetailsPage from './pages/users/user-details/user-details-page';

import './index.scss';
import { ProjectDetailsTabs } from './pages/projects/project-details/project-details';
import TeamDetailsPage from './pages/teams/team-details/team-details-page';
import { RouteDataProvider } from './render-routes/with-route-data';
import { resolveTenancyView, syncXstateWithRouteParams } from './utils';

export enum ManagerPages {
  HOME = 'Home',
  AUDIT_LOGS = 'AuditLogs',
  MY_ACCOUNT = 'MyAccount',
  ORGANIZATIONS = 'Organizations',
  ORGANIZATION_DETAILS = 'OrganizationDetails',
  CREATE_ORGANIZATION = 'CreateOrganization',
  PROJECTS = 'Projects',
  PROJECT_DETAILS_GENERAL_TAB = 'ProjectDetailsGeneral',
  PROJECT_DETAILS_ENVIRONMENTS_TAB = 'ProjectDetailsEnvironments',
  PROJECT_ENVIRONMENT_DETAILS = 'ProjectEnvironmentDetails',
  APPLET_DETAILS = 'AppletDetails',
  APPLET_PLAYGROUND = 'AppletPlayground',
  SUPER_ADMIN = 'SuperAdmin',
  TEAMS = 'Teams',
  TEAM_DETAILS = 'TeamDetails',
  USERS = 'Users',
  USER_DETAILS = 'UserDetails',
}

export const MainLayout: FC<PropsWithChildren> = () => {
  const { orgId } = useManagerCtxSelector((ctx) => ({
    orgId: ctx.selectedOrgId,
  }));
  const onSuccess = () => {
    sendUserEvent({ type: 'user.refetch' });
    sendManagerEvent({ type: 'manager.refetchUserSettings', orgId });
  };

  const homepageMatch = useMatch('/organization/:org_id');

  useEffect(() => {
    // switch browser icon on navigation home / other pages
    const linkPng: HTMLLinkElement =
      document.querySelector("link[rel='icon'][type='image/png']") ||
      document.createElement('link');
    const linkSvg: HTMLLinkElement =
      document.querySelector("link[rel='icon'][type='image/svg+xml']") ||
      document.createElement('link');
    const linkIco: HTMLLinkElement =
      document.querySelector("link[rel='shortcut icon']") ||
      document.createElement('link');
    const title =
      document.querySelector('title') || document.createElement('title');

    linkPng.type = 'image/png';
    linkPng.rel = 'icon';

    linkSvg.type = 'image/svg+xml';
    linkSvg.rel = 'icon';

    linkIco.rel = 'shortcut icon';

    if (homepageMatch) {
      linkPng.href = `/home-favicon-96x96.png`;
      linkSvg.href = `/home-favicon.svg`;
      linkIco.href = `/home-favicon.ico`;
      title.text = 'Pypestream Home';
    } else {
      linkPng.href = `/favicon-96x96.png`;
      linkSvg.href = `/favicon.svg`;
      linkIco.href = `/favicon.ico`;
      title.text = 'Pypestream Manager';
    }

    document.head.appendChild(linkPng);
    document.head.appendChild(linkSvg);
    document.head.appendChild(linkIco);
    document.head.appendChild(title);
  }, [homepageMatch]);

  return (
    <ErrorBoundary>
      <RouteDataProvider>
        <Page background="alt">
          <PrimaryNav />
          <Outlet />
          <ToastContainer stacked position="bottom-right" />
          <OnboardingModal onSuccess={onSuccess} />
        </Page>
      </RouteDataProvider>
    </ErrorBoundary>
  );
};

type WithTenancyEnforcementProps = PropsWithChildren & {
  accessFor: IPage['accessFor'];
};

export const WithTenancyEnforcement: FC<WithTenancyEnforcementProps> = ({
  children,
  accessFor,
}) => {
  const haveAccess = useAuthRole(accessFor || []);
  // The role check is only meaningful once the user machine has settled: either
  // loggedIn.loaded (profile + roles fetched) or loggedIn.error (fetch failed,
  // no roles will arrive) — see user.xstate.ts. Before that the user is either
  // bootstrapping a login (loggedIn.loading) or being signed out (loggedOut),
  // where ctx.user is cleared/incomplete and useAuthRole returns false, which
  // must NOT be shown as "not authorized". Gating on both settled states also
  // avoids trapping a user in an endless loader if the profile fetch errors.
  // useUserStateMatchesOneOf returns the matched state string (or false), so
  // coerce to a plain boolean for the pure decision helper.
  const isUserSettled = Boolean(
    useUserStateMatchesOneOf(['loggedIn.loaded', 'loggedIn.error'])
  );
  const isProtectedPage = Boolean(accessFor);
  const navigate = useNavigate();
  const { home } = useManagerCtxSelector((ctx) => ctx.routes);

  const view = resolveTenancyView({
    isProtectedPage,
    isUserSettled,
    haveAccess,
  });

  if (view === 'children') {
    return <>{children}</>;
  }

  // Auth is transitioning (logout redirect in flight, or roles still loading):
  // show a neutral loader instead of flashing the tenancy error. PSTUDIO-3559.
  // NOTE: the *absence* of this sub-500ms error flash on logout is only pinned
  // at the unit level (resolveTenancyView) — a Playwright assertion for it is
  // deferred: Manager e2e runs against the deployed bundle and a negative timing
  // race is inherently flaky. See docs/superpowers/plans for the coverage matrix.
  if (view === 'loader') {
    return (
      <PageBody background="none">
        <Spinner fullpage />
      </PageBody>
    );
  }

  return (
    <PageBody background="none">
      <ErrorState
        label="You are not authorized to see this page"
        body=""
        actionLabel="Return to Home"
        onAction={() => navigate(home)}
      />
    </PageBody>
  );
};

// https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

// const router = sentryCreateBrowserRouter(reactPages);

// The route elements below read XState, so they are components rather than
// values captured when the router is built: the router is built once (see
// RouterProvider) and would otherwise freeze whatever ctx held at boot.

// My Account has two entry points and they are NOT symmetrical.
//
// /organization/:org_id/my-account is the surface itself. It is where the
// user menu points (routes.myAccount) and where the auth app redirects back
// to after linking a login method (packages/auth/utils/flow-helpers.ts).
// Until PSTUDIO-4095 it was a <Navigate> into
// /organization/:org_id/users/:user_id, which made My Account the Users route
// under another URL; it is now a page of its own, so that removing the View
// Only floor (PSTUDIO-4099) cannot take a user's own profile away together
// with the Users page. It carries no user_id: the page always shows the
// logged-in user.
//
// /my-account is the org-less entry used from outside Manager
// (design-system's handleGoToMyAccount, which the "no access to this project"
// screen links to, and Pro Studio's user menu). It stays a redirect rather
// than a second mount of the page: RouteDataProvider rewrites every org-less
// path to /organization/<selectedOrgId>/... the moment the org is known, so
// mounting the page here would load the profile, be unmounted a frame later,
// and load it again — three GraphQL calls twice, and a discardUpdates in
// between. Redirecting sends the user straight to the canonical URL.
const renderMyAccountRoute = (key: string) => (
  <Route path="my-account" element={<MyAccountPage />} key={key} />
);

const OrgLessMyAccountRedirect = () => {
  const { loggedInUserOrgId } = useUserCtxSelector((ctx) => ({
    loggedInUserOrgId: ctx.user?.defaultAccount?.defaultOrgId,
  }));

  return loggedInUserOrgId ? (
    // `replace`: this is a redirect, not a page. Pushed, it stays in history
    // and Back lands on it, which pushes forward again — the same trap the
    // old users/:user_id redirect had before PSTUDIO-4095 replaced it.
    <Navigate to={`/organization/${loggedInUserOrgId}/my-account`} replace />
  ) : (
    <Spinner fullpage />
  );
};

const renderOrgLessMyAccountRedirect = (key: string) => (
  <Route path="my-account" element={<OrgLessMyAccountRedirect />} key={key} />
);

const UserDetailsRoute = () => {
  const { org_id: orgIdParam, user_id: userIdParam } = useParams();
  const { orgIdCtx, userIdCtx } = useManagerCtxSelector(
    ({ selectedOrgId, selectedUser }) => ({
      orgIdCtx: selectedOrgId,
      userIdCtx: selectedUser,
    })
  );

  return (
    <UserDetailsPage
      userId={userIdParam || userIdCtx || ''}
      orgId={orgIdParam || orgIdCtx || ''}
    />
  );
};

const EnvironmentDetailsRoute = () => {
  const { environment_id: environmentIdParam } = useParams();
  const environmentIdCtx = useManagerCtxSelector(
    ({ selectedEnvironment }) => selectedEnvironment
  );
  const environmentId = environmentIdParam || environmentIdCtx;

  return (
    <ProjectDetails
      title="Project Environment Details"
      background="secondary"
      tab={ProjectDetailsTabs.environments}
      hideDeleteButton
      key={environmentId}
      environmentId={environmentId}
    />
  );
};

export const RouterProvider = () => {
  // Build the router exactly once, in a state initializer (useMemo is only a
  // cache and may be recomputed). It used to be rebuilt on every render, and
  // this component re-renders on every manager/user machine change — which
  // RouteDataProvider makes right after each navigation, when it syncs route
  // params into ctx. Each rebuilt router re-initialised, re-ran the root loader
  // and dropped the navigation in flight: the URL changed but the old page stayed
  // (flicker, broken icons) until a refresh. pro-studio builds its router once
  // for the same reason.
  //
  // `useTransitions={false}` keeps router updates out of React.startTransition,
  // which React Router 7 otherwise uses by default. The XState selectors here are
  // plain setState subscriptions, so every machine update during a navigation
  // would interrupt the transition-priority render of the new page, and a burst
  // of them would hold the old page on screen under the new URL. This restores
  // the synchronous commit React Router 6 had.
  const [router] = useState(() =>
    sentryCreateBrowserRouter(
      createRoutesFromElements(
        <Route
          path="/"
          element={<MainLayout />}
          loader={({ params, request }) => {
            syncXstateWithRouteParams({
              params,
            });
            return null;
          }}
          // The root route's own pathname is always '/', so without this its
          // loader would run only on the first load: route params reach XState
          // on every navigation only because of this (as in pro-studio).
          shouldRevalidate={() => true}
        >
          {renderOrgLessMyAccountRedirect(`${ManagerPages.MY_ACCOUNT}-new`)}
          <Route path="/organization/:org_id">
            <Route index element={<Homepage />} path="" />
            <Route
              path="audit-logs"
              element={
                <WithTenancyEnforcement
                  accessFor={[
                    OrganizationRoleNames.ADMIN,
                    OrganizationRoleNames.SUPER_ADMIN,
                  ]}
                >
                  <LogsPage title="Logs" />
                </WithTenancyEnforcement>
              }
            />
            {renderMyAccountRoute(ManagerPages.MY_ACCOUNT)}
            <Route
              path="super-admin"
              element={
                <WithTenancyEnforcement
                  accessFor={[OrganizationRoleNames.SUPER_ADMIN]}
                >
                  <SuperAdminPage title="Super Admin" />
                </WithTenancyEnforcement>
              }
            />

            <Route path="child-orgs">
              <Route
                index
                element={
                  <WithTenancyEnforcement accessFor={MANAGER_SURFACE_ROLES}>
                    <OrganizationsPage title="Organizations" />
                  </WithTenancyEnforcement>
                }
                path=""
              />
              <Route
                path="create"
                element={
                  <WithTenancyEnforcement
                    accessFor={[
                      OrganizationRoleNames.ADMIN,
                      OrganizationRoleNames.SUPER_ADMIN,
                    ]}
                  >
                    <CreateOrganization title="Create Organization" />
                  </WithTenancyEnforcement>
                }
              />
            </Route>

            <Route
              path="edit"
              element={
                <WithTenancyEnforcement
                  accessFor={[
                    OrganizationRoleNames.ADMIN,
                    OrganizationRoleNames.SUPER_ADMIN,
                  ]}
                >
                  <OrganizationSettings title="Organization Settings" />
                </WithTenancyEnforcement>
              }
            />
            <Route
              path="users"
              key={ManagerPages.USERS}
              element={
                <WithTenancyEnforcement accessFor={MANAGER_SURFACE_ROLES}>
                  <Outlet />
                </WithTenancyEnforcement>
              }
            >
              <Route index element={<UsersPage title="Users" />} />
              <Route path=":user_id" element={<UserDetailsRoute />} />
            </Route>

            <Route
              path="teams"
              element={
                <WithTenancyEnforcement accessFor={MANAGER_SURFACE_ROLES}>
                  <Outlet />
                </WithTenancyEnforcement>
              }
            >
              <Route index element={<TeamsPage title="Teams" />} path="" />
              <Route
                path=":team_id"
                element={<TeamDetailsPage title="Team Details" />}
              />
            </Route>

            <Route
              path="projects"
              element={
                <WithTenancyEnforcement accessFor={MANAGER_SURFACE_ROLES}>
                  <Outlet />
                </WithTenancyEnforcement>
              }
              shouldRevalidate={() => {
                return true;
              }}
            >
              <Route index element={<ProjectsPage title="Projects" />} />

              <Route path=":project_id">
                <Route
                  element={
                    <ProjectDetails
                      title="Project Details"
                      background="secondary"
                      tab={ProjectDetailsTabs.general}
                    />
                  }
                  index
                />
                <Route path="environments">
                  <Route
                    element={
                      <ProjectDetails
                        title="Project Details"
                        background="secondary"
                        tab={ProjectDetailsTabs.environments}
                        hideDeleteButton
                      />
                    }
                    index
                  />
                  <Route path=":environment_id">
                    <Route index element={<EnvironmentDetailsRoute />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Last, so every real route above is tried first. A splat route is
              ranked below any route that names a path. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      )
    )
  );

  return <ReactRouterProvider router={router} useTransitions={false} />;
};
